import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addMonths, 
  subMonths,
  isSameMonth,
  isSameDay,
  getMonth,
  getYear,
  parseISO
} from 'date-fns';
import { getBusinessToday, toBusinessTimezone } from '../utils/dateTimeHelper';

const AttendanceScreen = () => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentDate, setCurrentDate] = useState(getBusinessToday());

  useEffect(() => {
    const month = String(getMonth(currentDate) + 1).padStart(2, '0');
    const year = String(getYear(currentDate));
    loadData(month, year);
  }, []);

  const loadData = async (month, year) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school/school-transaction/attendance/get-attendance-student?month=${month}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setAttendanceData(response.data || []);
    } catch (error) {
      console.error('AttendanceScreen loadData error:', error);
      handleCatch(error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
    const month = String(getMonth(newDate) + 1).padStart(2, '0');
    const year = String(getYear(newDate));
    loadData(month, year);
  };

  const handleNextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setCurrentDate(newDate);
    const month = String(getMonth(newDate) + 1).padStart(2, '0');
    const year = String(getYear(newDate));
    loadData(month, year);
  };

  const getAttendanceForDate = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return attendanceData.find((item) => {
      const itemDate = toBusinessTimezone(parseISO(item.attendanceDate));
      return format(itemDate, 'yyyy-MM-dd') === dateKey;
    });
  };

  const { presentCount, absentCount } = useMemo(() => {
    let present = 0;
    let absent = 0;
    for (const item of attendanceData) {
      const itemDate = toBusinessTimezone(parseISO(item.attendanceDate));
      if (!isSameMonth(itemDate, currentDate)) continue;
      if (item.attendance === 'P') present++;
      else if (item.attendance === 'A') absent++;
    }
    return { presentCount: present, absentCount: absent };
  }, [attendanceData, currentDate]);

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const calendar = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      calendar.push(day);
      day = addDays(day, 1);
    }

    const weeks = [];
    for (let i = 0; i < calendar.length; i += 7) {
      weeks.push(calendar.slice(i, i + 7));
    }

    return weeks;
  };

  const weeks = renderCalendar();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader title="My Attendance" showBack={true} />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
          p: 1,
        }}
      >
        {loading && attendanceData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            {/* Month navigation — 48px tall */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 48,
                px: 1,
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fff',
              }}
            >
              <IconButton onClick={handlePrevMonth} size="small" sx={{ color: colors.BLUE }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.BLUE }}>
                {format(currentDate, 'MMMM yyyy')}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small" sx={{ color: colors.BLUE }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                py: 1.25,
                px: 1,
                borderBottom: '1px solid #f0f0f0',
                bgcolor: '#FAFAFA',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#1565C0' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1565C0' }}>
                  Present: {presentCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#C62828' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#C62828' }}>
                  Absent: {absentCount}
                </Typography>
              </Box>
            </Box>

            {/* Day name header row */}
            <Box sx={{ display: 'flex', backgroundColor: '#F5F7FA' }}>
              {dayNames.map((dayName) => (
                <Box
                  key={dayName}
                  sx={{
                    width: 'calc(100% / 7)',
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#888',
                    py: 1,
                  }}
                >
                  {dayName}
                </Box>
              ))}
            </Box>

            {/* Calendar day cells */}
            <Box sx={{ px: 0.5, pb: 1 }}>
              {weeks.map((week, weekIndex) => (
                <Box key={weekIndex} sx={{ display: 'flex', mb: 0.5 }}>
                  {week.map((day) => {
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const attendance = getAttendanceForDate(day);
                    const isPresent = attendance?.attendance === 'P';
                    const isAbsent = attendance?.attendance === 'A';
                    const today = getBusinessToday();
                    const isDayToday = isSameDay(day, today);

                    return (
                      <Box
                        key={format(day, 'yyyy-MM-dd')}
                        sx={{
                          width: 'calc(100% / 7)',
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          bgcolor: isPresent
                            ? '#1565C0'
                            : isAbsent
                            ? '#C62828'
                            : 'transparent',
                          color: isPresent || isAbsent
                            ? '#fff'
                            : isCurrentMonth
                            ? '#333'
                            : '#ccc',
                          fontWeight: isPresent || isAbsent ? 700 : 400,
                          fontSize: 13,
                          border: isDayToday ? `2px solid ${colors.BLUE}` : 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        {format(day, 'd')}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                py: 1.5,
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#1565C0' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Present
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#C62828' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Absent
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    border: `2px solid ${colors.BLUE}`,
                    bgcolor: 'transparent',
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Today
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AttendanceScreen;
